import FormatColorFill from '@mui/icons-material/FormatColorFill';
import {
  DeleteColumn,
  DeleteRow,
  InsertColumnLeft,
  InsertColumnRight,
  InsertRowBottom,
  InsertRowTop,
  LayoutColumnFill,
  LayoutRowFill,
  MergeCellsHorizontal,
  SplitCellsHorizontal,
} from 'mui-tiptap/icons';
import { useRichTextEditorContext, MenuDivider, MenuButton, MenuControlsContainer } from 'mui-tiptap';
import React, { useMemo } from 'react';
import {
  AlignVerticalBottom,
  AlignVerticalCenter,
  AlignVerticalTop,
  BorderAll,
  BorderBottom,
  BorderClear,
  BorderLeft,
  BorderRight,
  BorderTop,
  FormatColorReset,
  TableChartOutlined,
  WidthFull,
  WidthNormal,
} from '@mui/icons-material';
import { getSelectedNodeAttributes } from '../tiptapHelper';
import { Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { ColorPalette, ColorName } from '../../ColorPalette';
import { theme } from '../../../../theme';
import { TableCellAttributes } from './plugins/TableCell';
import { Editor } from '@tiptap/core';

export type TableMenuControlsProps = {
  /** Class applied to the root controls container element. */
  className?: string;
  /**
   * Override the default labels for any of the menu buttons. For any value that
   * is omitted in this object, it falls back to the default content.
   */
  labels?: {
    insertColumnBefore?: string;
    insertColumnAfter?: string;
    deleteColumn?: string;
    insertRowAbove?: string;
    insertRowBelow?: string;
    deleteRow?: string;
    mergeCells?: string;
    splitCell?: string;
    toggleHeaderRow?: string;
    toggleHeaderColumn?: string;
    toggleHeaderCell?: string;
    deleteTable?: string;
  };
};

// Need to unset the attribute first, otherwise the attributes will not always be updated when multiple cells are selected
// This is possbibly a bug in tiptap
const forceSetCellAttribute = (editor: Editor | null, attribute: keyof TableCellAttributes, value: unknown) => {
  editor?.chain().focus().setCellAttribute(attribute, undefined).setCellAttribute(attribute, value).run();
};
const forceSetMultipleCellAttribute = (editor: Editor | null, attributes: (keyof TableCellAttributes)[], value: unknown) => {
  const chain = editor?.chain().focus();
  attributes.forEach((attribute) => {
    chain?.setCellAttribute(attribute, undefined).setCellAttribute(attribute, value);
  });
  chain?.run();
};

/**
 * Renders all of the controls for manipulating a table in a Tiptap editor
 * (add or delete columns or rows, merge cells, etc.).
 */
export default function TableMenuControls({ className, labels }: TableMenuControlsProps) {
  const editor = useRichTextEditorContext();

  const attrs: TableCellAttributes | null = useMemo(() => {
    if (editor?.state.selection) {
      const nodeAttrs = getSelectedNodeAttributes<TableCellAttributes>(editor.state.selection);
      if (nodeAttrs) {
        if (Array.isArray(nodeAttrs)) {
          return nodeAttrs.reduce<TableCellAttributes>(
            (acc, curr) => {
              acc.borderTop &&= curr.borderTop;
              acc.borderLeft &&= curr.borderLeft;
              acc.borderBottom &&= curr.borderBottom;
              acc.borderRight &&= curr.borderRight;
              return acc;
            },
            {
              borderTop: true,
              borderLeft: true,
              borderBottom: true,
              borderRight: true,
              backgroundColor: '',
              verticalAlign: 'top',
            } as TableCellAttributes,
          );
        } else {
          return nodeAttrs;
        }
      }
    }
    return null;
  }, [editor?.state.selection]);

  const handleColorChange = (color: ColorName | null) => {
    forceSetCellAttribute(editor, 'backgroundColor', color ?? '');
  };

  const handleOnBackgroundColorClick = () => {
    forceSetCellAttribute(editor, 'backgroundColor', '');
  };

  return (
    <MenuControlsContainer className={className}>
      <MenuButton
        tooltipLabel={'Sett inn kolonne før'}
        IconComponent={InsertColumnLeft}
        onClick={() => editor?.chain().focus().addColumnBefore().run()}
        disabled={!editor?.can().addColumnBefore()}
      />
      <MenuButton
        tooltipLabel={'Sett inn kolonne etter'}
        IconComponent={InsertColumnRight}
        onClick={() => editor?.chain().focus().addColumnAfter().run()}
        disabled={!editor?.can().addColumnAfter()}
      />
      <MenuButton
        tooltipLabel={'Slett kolonne'}
        IconComponent={DeleteColumn}
        onClick={() => editor?.chain().focus().deleteColumn().run()}
        disabled={!editor?.can().deleteColumn()}
      />
      <MenuDivider />
      <MenuButton
        tooltipLabel={'Sett inn rad over'}
        IconComponent={InsertRowTop}
        onClick={() => editor?.chain().focus().addRowBefore().run()}
        disabled={!editor?.can().addRowBefore()}
      />
      <MenuButton
        tooltipLabel={'Sett inn rad under'}
        IconComponent={InsertRowBottom}
        onClick={() => editor?.chain().focus().addRowAfter().run()}
        disabled={!editor?.can().addRowAfter()}
      />
      <MenuButton
        tooltipLabel={'Slett rad'}
        IconComponent={DeleteRow}
        onClick={() => editor?.chain().focus().deleteRow().run()}
        disabled={!editor?.can().deleteRow()}
      />
      <MenuDivider />
      <MenuButton
        tooltipLabel={'Slå sammen celler'}
        IconComponent={MergeCellsHorizontal}
        onClick={() => editor?.chain().focus().mergeCells().run()}
        disabled={!editor?.can().mergeCells()}
      />
      <MenuButton
        tooltipLabel={'Splitt celler'}
        IconComponent={SplitCellsHorizontal}
        onClick={() => editor?.chain().focus().splitCell().run()}
        disabled={!editor?.can().splitCell()}
      />
      <MenuDivider />
      <MenuButton
        tooltipLabel={'Venstre kantlinje'}
        IconComponent={BorderLeft}
        selected={attrs?.borderLeft}
        onClick={() => forceSetCellAttribute(editor, 'borderLeft', !attrs?.borderLeft)}
      />
      <MenuButton
        tooltipLabel={'Høyre kantlinje'}
        IconComponent={BorderRight}
        selected={attrs?.borderRight}
        onClick={() => forceSetCellAttribute(editor, 'borderRight', !attrs?.borderRight)}
      />
      <MenuButton
        tooltipLabel={'Øvre kantlinje'}
        IconComponent={BorderTop}
        selected={attrs?.borderTop}
        onClick={() => forceSetCellAttribute(editor, 'borderTop', !attrs?.borderTop)}
      />
      <MenuButton
        tooltipLabel={'Nedre kantlinje'}
        IconComponent={BorderBottom}
        selected={attrs?.borderBottom}
        onClick={() => forceSetCellAttribute(editor, 'borderBottom', !attrs?.borderBottom)}
      />
      <MenuButton
        tooltipLabel={'Alle kantlinjer'}
        IconComponent={BorderAll}
        onClick={() => forceSetMultipleCellAttribute(editor, ['borderLeft', 'borderRight', 'borderTop', 'borderBottom'], true)}
      />
      <MenuButton
        tooltipLabel={'Ingen kantlinjer'}
        IconComponent={BorderClear}
        onClick={() => forceSetMultipleCellAttribute(editor, ['borderLeft', 'borderRight', 'borderTop', 'borderBottom'], false)}
      />
      <MenuDivider />
      <MenuButton
        tooltipLabel={'Topp-justert'}
        IconComponent={AlignVerticalTop}
        selected={attrs?.verticalAlign === 'top'}
        onClick={() => forceSetCellAttribute(editor, 'verticalAlign', 'top')}
      />
      <MenuButton
        tooltipLabel={'Midt-justert'}
        IconComponent={AlignVerticalCenter}
        selected={attrs?.verticalAlign === 'middle'}
        onClick={() => forceSetCellAttribute(editor, 'verticalAlign', 'middle')}
      />
      <MenuButton
        tooltipLabel={'Bunn-justert'}
        IconComponent={AlignVerticalBottom}
        selected={attrs?.verticalAlign === 'bottom'}
        onClick={() => forceSetCellAttribute(editor, 'verticalAlign', 'bottom')}
      />
      <MenuDivider />
      <MenuButton
        tooltipLabel={'Marker headerrad'}
        IconComponent={LayoutRowFill}
        onClick={() => editor?.chain().focus().toggleHeaderRow().run()}
        disabled={!editor?.can().toggleHeaderRow()}
      />
      <MenuButton
        tooltipLabel={'Marker headerkolonne'}
        IconComponent={LayoutColumnFill}
        onClick={() => editor?.chain().focus().toggleHeaderColumn().run()}
        disabled={!editor?.can().toggleHeaderColumn()}
      />
      <MenuButton
        tooltipLabel={'Marker headercelle'}
        IconComponent={TableChartOutlined}
        onClick={() => editor?.chain().focus().toggleHeaderCell().run()}
        disabled={!editor?.can().toggleHeaderCell()}
        selected={editor?.isActive('tableHeader') ?? false}
      />
      <Tooltip
        slotProps={{
          tooltip: {
            sx: {},
          },
        }}
        arrow
        title={
          <Stack spacing={2}>
            <Typography variant="body2">Velg bakgrunnsfarge</Typography>

            <ColorPalette mode="set-color" onColorChange={handleColorChange} theme="light" />
            <Button variant="text" color="inherit" onClick={() => handleColorChange(null)} startIcon={<FormatColorReset fontSize="small" />}>
              Ingen farge
            </Button>
          </Stack>
        }>
        <IconButton
          sx={{
            borderRadius: 1,
            backgroundColor: attrs?.backgroundColor ? theme.palette.action.selected : undefined,
            color: attrs?.backgroundColor ? theme.palette.text.primary : undefined,
            cursor: attrs?.backgroundColor ? 'pointer' : 'default',
          }}
          size="small"
          onClick={handleOnBackgroundColorClick}>
          <FormatColorFill fontSize="small" />
        </IconButton>
      </Tooltip>
      <MenuDivider />
      <MenuButton
        tooltipLabel={'Normal bredde'}
        IconComponent={WidthNormal}
        onClick={() => editor?.chain().focus().setTableWidth(false).run()}
        selected={!editor?.getAttributes('table')['fullWidth']}
      />
      <MenuButton
        tooltipLabel={'Full bredde'}
        IconComponent={WidthFull}
        onClick={() => editor?.chain().focus().setTableWidth(true).run()}
        selected={editor?.getAttributes('table')['fullWidth'] ?? false}
      />
    </MenuControlsContainer>
  );
}
